// Helper di formattazione condivisi (date/HTML).
//
// Audit 2026-05-29 [LOW]: erano reimplementati per-feature (origine:
// ``features/messaggi/MessaggiPage.tsx``). Sono funzioni PURE e generiche,
// quindi vivono qui ed evitano divergenze tra feature. NB: gli helper con
// firma/output diversi (es. ``MailStatusBadge.formatDateTime`` con anno a 2
// cifre, ``SyncDetailPanel.formatRelative`` con segnale future/now) NON sono
// stati accorpati: cambierebbero il testo renderizzato (non e' un refactor a
// costo zero). Qui sta solo cio' che e' davvero riusabile 1:1.

/**
 * Converte un frammento HTML in testo semplice leggibile.
 *
 * - I tag di blocco/interruzione (``p``/``div``/``br``) diventano newline.
 * - Tutti gli altri tag vengono rimossi.
 * - Le entita' HTML piu' comuni vengono decodificate.
 *
 * Volutamente minimale (no DOM/parser): basta per le preview messaggi.
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<\/(p|div|br)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim();
}

/**
 * Formatta un importo con la sua valuta (bug-hunt 2026-07-13).
 *
 * EUR mantiene il look approvato ``€ 1.234,56`` (prefisso, byte-identico a
 * ``€ ${n.toLocaleString(...)}`` di prima → zero cambio visivo per il 99% degli
 * ordini). Le altre valute mostrano il CODICE ISO dopo il numero
 * (``1.234,56 GBP``): non inventiamo simbolo/posizione sbagliati, e l'importo
 * non viene più mislabellato come euro (regola «degradazione onesta»).
 */
export function formatMoney(amount: number, currency?: string | null): string {
  const code = (currency || "EUR").toUpperCase();
  const n = amount.toLocaleString("it-IT", { minimumFractionDigits: 2 });
  return code === "EUR" ? `€ ${n}` : `${n} ${code}`;
}

/**
 * Data odierna LOCALE in ISO ``YYYY-MM-DD`` — default per ``<input type="date">``
 * su data documento/fattura/rimborso.
 *
 * NON usare ``new Date().toISOString().slice(0,10)``: quello è in UTC e nelle
 * ore notturne italiane restituisce il giorno PRECEDENTE, con impatto fiscale
 * sulla data del documento. Costruita dai getter locali (getFullYear/Month/Date)
 * invece che da ``toLocaleDateString('en-CA')`` per essere deterministica anche
 * senza dati ICU completi (build Node minimali cadrebbero su ``MM/DD/YYYY``).
 */
export function todayLocalISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Formatta un timestamp ISO come "GG/MM, HH:MM" (locale it-IT).
 *
 * Ritorna stringa vuota se il valore e' nullo e l'input grezzo se la data
 * non e' parsabile (fail-soft: non si perde l'informazione a video).
 */
export function formatDateTime(value: string | null): string {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

/**
 * Data italiana con anno ("12/06/2026"); ``fallback`` se il valore e' nullo.
 *
 * Unica implementazione: prima ce n'erano DUE esportate con questo stesso nome
 * (``clienti/api.ts`` e ``fatturazione/components/columns.tsx``) e la seconda
 * usava ``toLocaleDateString("it-IT")`` nudo — il cui formato dipende dai dati
 * ICU del runtime (21/7 contro 21/07), quindi le stesse date si rendevano
 * diverse a seconda della pagina. Intl con day/month "2-digit" e' deterministico.
 *
 * A differenza di ``formatDateTime`` (GG/MM + ora) porta l'ANNO: un cliente puo'
 * non ordinare da mesi, e una fattura ha una data fiscale.
 *
 * Data non parsabile → il valore GREZZO, non il fallback: fail-soft, il dato
 * non si perde e l'anomalia resta visibile. Era gia' cosi' in due delle tre
 * copie; la terza (clienti) tornava "" ed e' l'unico comportamento cambiato.
 */
export function formatDateIt(value: string | null | undefined, fallback = ""): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/**
 * Formatta un timestamp ISO come tempo relativo compatto rispetto a ora:
 * "ora", "Xm", "Xh", "Xg" e, oltre la settimana, la data "GG/MM" (it-IT).
 *
 * Ritorna stringa vuota se il valore e' nullo o non parsabile.
 */
export function formatRelative(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "ora";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}g`;
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit" }).format(date);
}
